import React, { useState, useEffect } from 'react';
import { Star, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { adminStoresAPI, adminFeaturedStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const FeaturedStoresManagement = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await adminStoresAPI.getAll();
      setStores(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar las tiendas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = async (storeId, currentFeatured) => {
    try {
      const newFeatured = !currentFeatured;
      await adminFeaturedStoresAPI.updateFeature(storeId, {
        is_featured: newFeatured,
        featured_order: newFeatured ? stores.filter(s => s.isFeatured).length + 1 : null,
      });
      toast({
        title: "Éxito",
        description: newFeatured ? "Tienda destacada" : "Tienda removida de destacadas",
      });
      loadStores();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al actualizar tienda destacada",
        variant: "destructive",
      });
    }
  };

  const handleUpdateOrder = async (storeId, newOrder) => {
    try {
      await adminFeaturedStoresAPI.updateFeature(storeId, {
        featured_order: parseInt(newOrder) || 0,
      });
      toast({
        title: "Éxito",
        description: "Orden actualizado exitosamente",
      });
      loadStores();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Error al actualizar orden",
        variant: "destructive",
      });
    }
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div>
        <h2 className="text-2xl font-bold text-white">Tiendas Destacadas</h2>
        <p className="text-[#A2A2A2]">Gestiona las tiendas destacadas y su orden</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A2A2A2] w-5 h-5" />
        <Input
          type="text"
          placeholder="Buscar por nombre de tienda..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A2A2A2]">Cargando...</div>
      ) : filteredStores.length === 0 ? (
        <div className="text-center py-12 text-[#A2A2A2]">No hay tiendas disponibles</div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Tienda</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Destacada</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Orden</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredStores.map((store) => (
                <tr key={store.id} className="hover:bg-white/5">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {store.logo && (
                        <img src={store.logo} alt={store.name} className="w-12 h-12 object-cover rounded-lg" />
                      )}
                      <div>
                        <div className="text-white font-medium">{store.name}</div>
                        <div className="text-[#A2A2A2] text-sm">{store.category}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={store.isFeatured}
                        onCheckedChange={() => handleToggleFeatured(store.id, store.isFeatured)}
                      />
                      {store.isFeatured && (
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {store.isFeatured ? (
                      <input
                        type="number"
                        min="0"
                        value={store.featured_order || 0}
                        onChange={(e) => handleUpdateOrder(store.id, e.target.value)}
                        className="w-20 px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/20"
                      />
                    ) : (
                      <span className="text-[#A2A2A2]">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedStoresManagement;

